import { useCallback, useRef } from 'react';

import customNanoId from 'lib/nanoid/nanoid';
import ActivitySheet from '../../shared/sheet/activity-sheet';
import ActivitySheetSaveStatus from '../../shared/sheet/activity-sheet-save-status';
import SubActivityView from '../../sub-activity/sub-activity-view';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../interactive-response.style';
import InteractiveResponseStart from '../shared/interactive-response-start';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import { useBatchStudentAnswersContext } from '../../shared/batch-student-answer.context';
import { useStudentGetAnswers } from 'approot/activity-builder/student/answer/student-answer.hooks';
import { DropItem } from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import { ViewProps } from './interactive-response-categorise.constants';
import InteractiveResponseCategoriseAnswers from './interactive-response-categorise-answers';
import InteractiveResponseCategoriseLargeView from './interactive-response-categorise-large-view';
import InteractiveResponseCategoriseSmallView from './interactive-response-categorise-small-view';
import {
  prepareItemsForSave,
  prepareSmallItemsForSave,
} from './interactive-response-categorise.utils';
import { DropItem as DropItemSmall } from './play/interactive-response-categorise-small.constants';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE } from 'global/constants';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

const InteractiveResponseCategoriseStudent = (
  props: ViewProps & {
    isLoggedIn: boolean;
  }
) => {
  const { id, type, subActivityProperties, isLoggedIn } = props;

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { openModal, closeModal, isVisible } = useSimpleModalState();
  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();

  const { isLesson, setActiveComponent } = useActivityBuilderPresentContext();

  const { componentAnswers, variables, hasFetchedAnswers, error } =
    useStudentGetAnswers({
      componentId: id,
      skip: !isLoggedIn,
    });

  const { handleUpdate } = useBatchStudentAnswersContext();

  const handleSaveAttempt = useCallback(
    (dropItems: DropItem[]) => {
      if (
        isLoggedIn &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          dataTrackingComponentType:
            ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: null,
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: prepareItemsForSave(
                dropItems,
                props.properties.interactiveResponseCategorise.items
              ),
            },
          },
        });
      }
    },
    [id, subActivityProperties, variables, isLoggedIn, handleUpdate, props]
  );

  const handleSmallSaveAttempt = useCallback(
    (dropItems: DropItemSmall[]) => {
      if (
        isLoggedIn &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: null,
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: prepareSmallItemsForSave(dropItems),
            },
          },
        });
      }
    },
    [id, subActivityProperties, variables, isLoggedIn, handleUpdate]
  );

  return (
    <>
      <SubActivityView
        subActivityDetails={subActivityProperties}
        componentId={id}
        componentType={type}
        onClick={() => setActiveComponent({ id })}
      >
        <InteractiveResponseContainer id={getComponentSelectorId(id)}>
          <InteractiveResponseCategoriseAnswers
            {...props}
            answerAttempt={componentAnswers?.[0]}
            isLoading={isLoggedIn && !hasFetchedAnswers}
          />
          <InteractiveResponseStart
            isLoggedIn={isLoggedIn}
            hasFetchedAnswers={hasFetchedAnswers}
            error={error}
            label="Matching"
            title={props.properties.interactiveResponseCategorise.title || ''}
            onStart={() => {
              openModal();
              startGame();
            }}
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          hasBackgroundColor
          title={props.properties.interactiveResponseCategorise.title || ''}
          customActions={
            <ActivitySheetSaveStatus
              title={props.properties.interactiveResponseCategorise.title || ''}
              onClose={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  closeModal();
                }
              }}
            />
          }
        >
          {isMediaUpSm ? (
            <InteractiveResponseCategoriseLargeView
              {...props}
              isPlaying={isPlaying}
              finishGame={finishGame}
              onAnswerAttempt={handleSaveAttempt}
              attemptAnswers={
                componentAnswers?.[0]
                  ?.answer as InteractiveResponseAnswerFragment
              }
              setCloseModalCallback={callback => {
                closeModalCallbackRef.current = callback;
              }}
              showAnswer={isLesson}
            />
          ) : (
            <InteractiveResponseCategoriseSmallView
              {...props}
              isPlaying={isPlaying}
              finishGame={finishGame}
              playClick={playClick}
              playStart={playStart}
              playAudio={playAudio}
              toggleAudio={toggleAudio}
              onAnswerAttempt={handleSmallSaveAttempt}
              attemptAnswers={
                componentAnswers?.[0]
                  ?.answer as InteractiveResponseAnswerFragment
              }
              showAnswer={isLesson}
            />
          )}
        </ActivitySheet>
      )}
    </>
  );
};

export default InteractiveResponseCategoriseStudent;
